import React from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { formatCurrency, formatAbbreviatedNumber } from '../../utils/format';
import { useLanguage } from '../../contexts/LanguageContext';

// ─── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ tk, title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16 }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 600, color: tk.text, marginBottom: 2 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: tk.textMuted }}>{subtitle}</div>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8 }}>{actions}</div>}
    </div>
  );
}

// ─── StatStrip ────────────────────────────────────────────────────────────────
export function StatStrip({ tk, items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: 12, marginBottom: 16 }}>
      {items.map((s, i) => (
        <div key={i} style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 12, padding: '12px 14px' }}>
          <div style={{ fontSize: 10.5, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
            {s.label}
          </div>
          {s.money ? (
            <MonoMoney amount={s.value} tk={tk} size={18} weight={700} />
          ) : (
            <div style={{ fontSize: 18, fontWeight: 700, fontFamily: '"JetBrains Mono", monospace', color: tk.text }}>{s.value}</div>
          )}
          {s.delta && (
            <div style={{ fontSize: 10.5, fontWeight: 600, marginTop: 4, color: s.positive ? tk.successText : tk.dangerText, fontFamily: '"JetBrains Mono", monospace' }}>
              {s.delta}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── PrimaryBtn ───────────────────────────────────────────────────────────────
export function PrimaryBtn({ tk, children, onClick, icon: Icon, disabled, type = 'button', ...rest }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '0 14px', height: 34,
        background: tk.accent, color: '#fff',
        border: 'none', borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600,
        opacity: disabled ? 0.55 : 1,
      }}
      {...rest}
    >
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
}

// ─── GhostBtn ────────────────────────────────────────────────────────────────
export function GhostBtn({ tk, children, onClick, icon: Icon, disabled, type = 'button', ...rest }) {
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '0 12px', height: 34,
        background: tk.surface, color: tk.textSecondary,
        border: `1px solid ${tk.border}`, borderRadius: 8, cursor: disabled ? 'default' : 'pointer',
        fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
        opacity: disabled ? 0.55 : 1,
      }}
      {...rest}
    >
      {Icon && <Icon size={13} />}
      {children}
    </button>
  );
}

// ─── Toolbar ─────────────────────────────────────────────────────────────────
export function Toolbar({
  tk,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  filters,
  actions,
  children,
  searchLabel,
  searchColumnStyle,
  searchWrapProps,
  searchInputProps,
}) {
  const hasSearch = searchPlaceholder !== undefined;
  const searchLabelStyle = searchLabel
    ? {
      fontSize: 10.5,
      color: tk.textMuted,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    }
    : null;

  const searchInnerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '0 10px',
    height: 34,
    background: tk.surface,
    border: `1px solid ${tk.border}`,
    borderRadius: 8,
    fontSize: 12,
    color: tk.textMuted,
  };

  const searchInputEl = onSearchChange ? (
    <input
      value={searchValue || ''}
      onChange={(e) => onSearchChange(e.target.value)}
      placeholder={searchPlaceholder}
      style={{
        background: 'transparent',
        border: 'none',
        outline: 'none',
        fontSize: 12,
        color: tk.text,
        flex: 1,
        minWidth: 0,
        fontFamily: 'inherit',
      }}
      {...(searchInputProps || {})}
    />
  ) : (
    <span style={{ flex: 1, minWidth: 0 }}>{searchPlaceholder}</span>
  );

  const searchWrapStyled = searchWrapProps || {};
  const { style: searchWrapStyle, ...searchWrapRest } = searchWrapStyled;

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', gap: 8,
      marginBottom: 12, width: '100%', overflow: 'hidden',
    }}>
      {/* Search — full width unless searchLabel narrows it */}
      {(searchPlaceholder !== undefined) && (
        searchLabel ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: '0 1 220px',
              maxWidth: 280,
              minWidth: 140,
              ...searchColumnStyle,
            }}
          >
            <span style={searchLabelStyle}>{searchLabel}</span>
            <div style={{ ...searchInnerStyle, width: '100%', boxSizing: 'border-box', ...searchWrapStyle }} {...searchWrapRest}>
              <Search size={13} color={tk.textMuted} style={{ flexShrink: 0 }} />
              {searchInputEl}
            </div>
          </div>
        ) : (
          <div style={{
            ...searchInnerStyle,
            flex: 1,
            flexBasis: 0,
            minWidth: 160,
            ...searchWrapStyle,
          }} {...searchWrapRest}>
            <Search size={13} color={tk.textMuted} style={{ flexShrink: 0 }} />
            {searchInputEl}
          </div>
        )
      )}

      {/* Fixed-width filters — no wrapping */}
      {filters && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 8,
          flexShrink: hasSearch ? 0 : 1,
          flex: hasSearch ? undefined : 1,
          minWidth: 0,
          width: hasSearch ? undefined : '100%',
          justifyContent: 'flex-start',
        }}>
          {filters}
        </div>
      )}
      {children && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
          {children}
        </div>
      )}

      {/* Actions pushed to the right */}
      {actions && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
          {actions}
        </div>
      )}
    </div>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────
let _fcCounter = 0;
export function FilterChip({ tk, label, value, active, onChange, options, onFocus }) {
  if (options && onChange) {
    const selectId = `fc-${(label || '').replace(/\s/g, '')}-${++_fcCounter}`;
    return (
      <>
        <style>{`
          #${selectId} {
            width: 100%; min-width: 0; box-sizing: border-box;
            height: 34px; padding: 0 28px 0 10px;
            background-color: ${active ? tk.accentSoft : tk.surface} !important;
            color: ${active ? tk.accentText : tk.textSecondary} !important;
            border: 1px solid ${active ? tk.accent : tk.border};
            border-radius: 8px; font-family: inherit; font-size: 12px; font-weight: 500;
            cursor: pointer; outline: none; appearance: none; -webkit-appearance: none;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%238a8a96' stroke-width='2' viewBox='0 0 24 24'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
            background-repeat: no-repeat; background-position: right 8px center;
          }
          #${selectId} option {
            background-color: ${tk.surface} !important;
            color: ${tk.text} !important;
          }
        `}</style>
        <select
          id={selectId}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={onFocus}
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </>
    );
  }
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '0 10px', height: 34,
      background: active ? tk.accentSoft : tk.surface,
      color: active ? tk.accentText : tk.textSecondary,
      border: `1px solid ${active ? tk.accent : tk.border}`, borderRadius: 8,
      fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
    }}>
      <span>{label}</span>
      {value && <span style={{ fontWeight: 600, color: active ? tk.accentText : tk.text }}>{value}</span>}
      <ChevronDown size={11} />
    </div>
  );
}

// ─── StatusPill ───────────────────────────────────────────────────────────────
const STATUS_STYLES = {
  open:      (tk) => ({ bg: tk.accentSoft,   fg: tk.accentText }),
  ordered:   (tk) => ({ bg: tk.accentSoft,   fg: tk.accentText }),
  active:    (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  paid:      (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  approved:  (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  received:  (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  delivered: (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  reviewed:  (tk) => ({ bg: tk.successSoft,  fg: tk.successText }),
  closed:    (tk) => ({ bg: tk.sunken,       fg: tk.textSecondary }),
  ended:     (tk) => ({ bg: tk.sunken,       fg: tk.textSecondary }),
  pending:   (tk) => ({ bg: tk.warningSoft,  fg: tk.warning }),
  transit:   (tk) => ({ bg: tk.warningSoft,  fg: tk.warning }),
  invited:   (tk) => ({ bg: tk.warningSoft,  fg: tk.warning }),
  force_closed:(tk)=>({ bg: tk.warningSoft,  fg: tk.warning }),
  refunded:  (tk) => ({ bg: tk.dangerSoft,   fg: tk.dangerText }),
  rejected:  (tk) => ({ bg: tk.dangerSoft,   fg: tk.dangerText }),
  cancelled: (tk) => ({ bg: tk.dangerSoft,   fg: tk.dangerText }),
  out:       (tk) => ({ bg: tk.dangerSoft,   fg: tk.dangerText }),
};

export function StatusPill({ tk, kind, children }) {
  const getStyle = STATUS_STYLES[kind] || ((tk) => ({ bg: tk.sunken, fg: tk.textSecondary }));
  const s = getStyle(tk);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '2px 8px', borderRadius: 4,
      fontSize: 10.5, fontWeight: 600,
      background: s.bg, color: s.fg,
      textTransform: 'uppercase', letterSpacing: 0.4,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 99, background: s.fg, opacity: 0.7, flexShrink: 0 }} />
      {children}
    </span>
  );
}

// ─── DataTable ────────────────────────────────────────────────────────────────
export function DataTable({ tk, columns, rows, renderCell, emptyMessage }) {
  if (!rows || rows.length === 0) {
    return (
      <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 12, padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 13, color: tk.textMuted }}>{emptyMessage || 'No data found'}</div>
      </div>
    );
  }
  return (
    <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 12, overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${tk.border}`, background: tk.surfaceAlt }}>
              {columns.map((c, i) => (
                <th key={i} style={{
                  // Everything start-aligns: leftmost in LTR, rightmost in RTL — header matches cells.
                  textAlign: 'start',
                  padding: '10px 16px',
                  fontSize: 10.5, fontWeight: 600, color: tk.textMuted,
                  textTransform: 'uppercase', letterSpacing: 0.5,
                  whiteSpace: 'nowrap',
                }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri} style={{ borderBottom: ri < rows.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
                {columns.map((c, ci) => {
                  // Action columns must never wrap or clip; text columns truncate with "…".
                  const noTruncate = c.noTruncate || c.key === 'actions';
                  return (
                    <td key={ci} style={{
                      padding: '11px 16px',
                      textAlign: 'start',
                      verticalAlign: 'middle',
                      color: tk.textSecondary,
                      whiteSpace: 'nowrap',
                    }}>
                      {noTruncate ? renderCell(row, c.key, ri) : (
                        <div style={{
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          maxWidth: c.maxWidth || 'clamp(120px, 28vw, 340px)',
                        }}>
                          {renderCell(row, c.key, ri)}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ tk, currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 16 }}>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        style={{
          padding: '6px 12px', height: 32, borderRadius: 7,
          background: tk.surface, border: `1px solid ${tk.border}`,
          color: currentPage === 1 ? tk.textMuted : tk.textSecondary,
          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
        }}
      >←</button>
      <span style={{ fontSize: 12, color: tk.textMuted, fontFamily: '"JetBrains Mono", monospace' }}>
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        style={{
          padding: '6px 12px', height: 32, borderRadius: 7,
          background: tk.surface, border: `1px solid ${tk.border}`,
          color: currentPage === totalPages ? tk.textMuted : tk.textSecondary,
          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
        }}
      >→</button>
    </div>
  );
}

// ─── MonoMoney — currency display with JetBrains Mono ─────────────────────────
// Amount first, currency label after: label lands right of the price in LTR
// and left of the price in RTL.
export function MonoMoney({ amount, tk, size = 14, weight = 500, color, abbreviate = false }) {
  const { t } = useLanguage();
  const displayColor = color || tk.text;
  const val = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  const formatted = abbreviate
    ? formatAbbreviatedNumber(val, 3)
    : val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <span style={{ fontFamily: '"JetBrains Mono", monospace', fontVariantNumeric: 'tabular-nums', fontSize: size, color: displayColor, fontWeight: weight, whiteSpace: 'nowrap' }}>
      {formatted}
      <span style={{ fontSize: Math.max(9, size - 4), opacity: 0.6, marginInlineStart: 3 }}>{t('currency.egp')}</span>
    </span>
  );
}

// ─── AvatarInitials ───────────────────────────────────────────────────────────
export function AvatarInitials({ name, tk, size = 28 }) {
  const initials = (name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div style={{
      width: size, height: size, borderRadius: Math.floor(size * 0.25),
      background: tk.accentSoft, color: tk.accentText,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.floor(size * 0.42), fontWeight: 600, flexShrink: 0,
    }}>
      {initials}
    </div>
  );
}

// ─── MethodPill — payment method badge ────────────────────────────────────────
export function MethodPill({ tk, method, t }) {
  const label = t ? (t(`pos.${method}`) || method) : method;
  return (
    <span style={{
      display: 'inline-block', fontSize: 10.5, fontWeight: 600,
      padding: '2px 7px', borderRadius: 4,
      background: tk.surfaceAlt, color: tk.textSecondary,
      textTransform: 'capitalize',
    }}>{label}</span>
  );
}
